import { useCallback, useEffect, useRef, useState } from 'react';
import auth from '@client/components/auth';
import { Async } from '@client/new/helpers/types';
import {
  notifyCommunityUnreadChanged,
  useCommunityIndicator,
} from '@client/new/hooks/use-community-indicator';
import { CommunityId } from '../model/community.types';
import { Post, PostId } from '../model/post.types';

const SCROLL_TOP_THRESHOLD_PX = 16;

type NewPostsIndicator = {
  hasNewPosts: boolean;
  onLoadNewPosts: () => void;
};

type SeenTopPost = { id: PostId; createdAtMs: number };
type TopPost = { id: PostId; isOwn: boolean; createdAtMs: number };
type NewPostsAction = 'acknowledge' | 'show' | 'clear' | 'none';

// Decides what the indicator should do for the current top post, given the
// last-acknowledged baseline and whether the viewer is scrolled to the top.
// Pure so the branching can be tested without rendering or window globals.
export const decideNewPostsAction = (
  seen: SeenTopPost | null,
  top: TopPost | null,
  enabled: boolean,
  scrollAtTop: boolean,
): NewPostsAction => {
  if (!enabled) return 'clear';
  if (top === null) return 'none';
  if (seen === null || top.isOwn) return 'acknowledge';
  if (top.id === seen.id) return 'clear';
  if (top.createdAtMs > seen.createdAtMs) {
    return scrollAtTop ? 'acknowledge' : 'show';
  }
  return 'none';
};

export const useNewPostsIndicator = (
  communityId: CommunityId,
  posts: Async<Post[]>,
  // The unread baseline must only follow the canonical, unfiltered feed. While
  // a search or author filter is active the "top post" is not the community's
  // newest post, so acknowledging it would corrupt last_post_id.
  enabled = true,
): NewPostsIndicator => {
  const { setLastSeenPostId } = useCommunityIndicator();
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const seenTopPostRef = useRef<SeenTopPost | null>(null);

  const topPost =
    posts.type === 'fetched' && posts.value.length > 0 ? posts.value[0] : null;
  const topPostId = topPost === null ? null : topPost.id;
  const topPostIsOwn = topPost === null ? false : topPost.author.isOwn;
  const topPostCreatedAtMs =
    topPost === null ? null : topPost.createdAt.getTime();

  const acknowledgeTopPost = useCallback(() => {
    if (topPostId === null || topPostCreatedAtMs === null) return;
    seenTopPostRef.current = { id: topPostId, createdAtMs: topPostCreatedAtMs };
    setHasNewPosts(false);
    if (!auth.isAdministrator() && !auth.isModerator()) {
      setLastSeenPostId(topPostId);
    }
  }, [topPostId, topPostCreatedAtMs, setLastSeenPostId]);

  useEffect(() => {
    seenTopPostRef.current = null;
    setHasNewPosts(false);
  }, [communityId]);

  useEffect(() => {
    const top =
      topPostId === null || topPostCreatedAtMs === null
        ? null
        : {
            id: topPostId,
            isOwn: topPostIsOwn,
            createdAtMs: topPostCreatedAtMs,
          };
    const action = decideNewPostsAction(
      seenTopPostRef.current,
      top,
      enabled,
      window.scrollY <= SCROLL_TOP_THRESHOLD_PX,
    );

    if (action === 'acknowledge') {
      acknowledgeTopPost();
    } else if (action === 'clear') {
      setHasNewPosts(false);
    } else if (action === 'show') {
      setHasNewPosts(true);
      notifyCommunityUnreadChanged();
    }
  }, [
    enabled,
    topPostId,
    topPostIsOwn,
    topPostCreatedAtMs,
    acknowledgeTopPost,
  ]);

  const onLoadNewPosts = useCallback(() => {
    acknowledgeTopPost();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [acknowledgeTopPost]);

  useEffect(() => {
    if (!hasNewPosts) return;
    const handleWindowScroll = () => {
      if (window.scrollY <= SCROLL_TOP_THRESHOLD_PX) {
        acknowledgeTopPost();
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [hasNewPosts, acknowledgeTopPost]);

  return { hasNewPosts, onLoadNewPosts };
};
